import PageHeader from "@components/PageHeader";
import { makeSelectAccess } from "@features/auth/authSlice";
import { useLoadActiveCampaignOrderCountQuery } from "@features/admin/orderManage/api";
import { APP_NAME } from "@utils/constants";
import React from "react";
import { Helmet } from "react-helmet";
import {
  RiBox1Line,
  RiMegaphoneLine,
  RiPulseLine,
  RiRefreshLine,
  RiShapesLine,
  RiShoppingCartLine,
} from "react-icons/ri";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

const SellerDashboardOverView = () => {
  const access = useSelector(makeSelectAccess);
  const { data: countData, isLoading, isFetching } = useLoadActiveCampaignOrderCountQuery(
    undefined,
    { pollingInterval: 15000 }
  );

  const activeOrderCount = countData?.data?.count ?? 0;
  const activeCampaignsCount = countData?.data?.active_campaigns_count ?? 0;

  return (
    <>
      <Helmet>
        <title>{APP_NAME}</title>
      </Helmet>

      {/* Realtime Active Campaign Order Count Card - Plain Simple Styling */}
      <div className="mb-6 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
              </span>
              <span className="text-xs font-semibold uppercase tracking-wider text-green-700 bg-green-50 px-2.5 py-1 rounded border border-green-200 flex items-center gap-1">
                <RiPulseLine className="animate-pulse" /> Realtime Order Count
              </span>
              {isFetching && (
                <span className="text-xs text-gray-500 flex items-center gap-1 animate-pulse ml-2">
                  <RiRefreshLine className="animate-spin" /> Updating...
                </span>
              )}
            </div>
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
              Active Campaign Orders
            </h2>
            <p className="text-sm text-gray-600">
              Live order count associated with currently active sales campaigns
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="bg-gray-50 px-6 py-3.5 rounded-lg border border-gray-200 flex flex-col items-center justify-center min-w-[140px]">
              <span className="text-3xl font-extrabold text-blue-600 tracking-tight">
                {isLoading ? (
                  <span className="inline-block w-12 h-8 bg-gray-200 animate-pulse rounded" />
                ) : (
                  activeOrderCount
                )}
              </span>
              <span className="text-xs text-gray-600 font-medium mt-1 uppercase tracking-wider">
                Active Orders
              </span>
            </div>

            <div className="bg-gray-50 px-5 py-3.5 rounded-lg border border-gray-200 flex flex-col items-center justify-center min-w-[130px]">
              <span className="text-2xl font-bold text-gray-800">
                {isLoading ? (
                  <span className="inline-block w-8 h-6 bg-gray-200 animate-pulse rounded" />
                ) : (
                  activeCampaignsCount
                )}
              </span>
              <span className="text-xs text-gray-600 font-medium mt-1 uppercase tracking-wider">
                Campaigns
              </span>
            </div>

            <Link
              to="/admin/order-manage/campaign"
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-3.5 rounded-lg shadow-sm transition-all flex items-center gap-2"
            >
              <RiShoppingCartLine size={18} />
              View Orders
            </Link>
          </div>
        </div>
      </div>

      <PageHeader title="Dashboard Links" />
      <div className="grid grid-cols-4 gap-4">
        {links
          .filter((each) => access[each.path])
          .map((each) => (
            <Link
              to={each.path}
              key={each.path}
              className="bg-white rounded-xl w-full shadow-sm border border-gray-200 gap-2 flex justify-center flex-col items-center py-8 hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-pointer group"
            >
              <div className="p-3 rounded-full bg-gray-50 text-gray-700 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                {each.icon}
              </div>
              <div className="text-base font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">
                {each.label}
              </div>
            </Link>
          ))}
      </div>
    </>
  );
};

export default SellerDashboardOverView;

const links = [
  {
    path: "/admin/product-manage/main",
    icon: <RiBox1Line size="26" />,
    label: "Main Products",
  },
  {
    path: "/admin/product-manage/bundle",
    icon: <RiBox1Line size="30" />,
    label: "Bundle Products",
  },
  {
    path: "/admin/scheme-manage/pre-order",
    icon: <RiMegaphoneLine size="26" />,
    label: "Pre-Order Scheme",
  },
  {
    path: "/admin/scheme-manage/campaign",
    icon: <RiMegaphoneLine size="30" />,
    label: "Campaign Scheme",
  },
  {
    path: "/admin/order-manage/pre-order",
    icon: <RiShoppingCartLine size="26" />,
    label: "Pre-Order Orders",
  },
  {
    path: "/admin/order-manage/campaign",
    icon: <RiShoppingCartLine size="30" />,
    label: "Campaign Orders",
  },
  {
    path: "/admin/home-page-groups",
    icon: <RiShapesLine size="30" />,
    label: "Homepage Groups",
  },
];



